import { TextFieldProps, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

interface Props extends TextFieldProps<'outlined'> {
  readonly helperText?: string;
  readonly error?: boolean;
  readonly maxLength: number;
}

export const LimitedTextField: React.FC<Props> = (props: Props): React.ReactElement => {
  const [characterCount, setCharacterCount] = React.useState<number>(0);

  const { maxLength = 200, ...passthroughProps } = props;

  const ref = React.useRef<HTMLInputElement>(null);
  const slotProps = React.useMemo(
    (): any => ({
      htmlInput: {
        maxLength: maxLength,
        ref: ref,
      },
      input: {
        endAdornment: (
          <InputAdornment position="end" sx={styles.adornment}>
            <Typography variant="body1" color="custom.LightGray">
              {characterCount}/{maxLength}
            </Typography>
          </InputAdornment>
        ),
        sx: styles.input,
      },
    }),
    [maxLength, characterCount],
  );

  React.useEffect((): void | VoidFunction => {
    const { current: input } = ref;
    if (input === null) {
      return;
    }

    const characterCounter = (): void => {
      const { value } = input;
      setCharacterCount(value?.length);
    };

    input.addEventListener('input', characterCounter);

    return (): void => {
      input.removeEventListener('input', characterCounter);
    };
  }, []);

  return (
    <Box display="flex" flexDirection="column">
      <TextField {...passthroughProps} slotProps={slotProps} />
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  adornment: {
    alignSelf: 'flex-end',
    mb: 0.5,
    pointerEvents: 'none',
    color: 'text.secondary',
  },
  input: {
    pr: 8,
  },
};
